import { create } from 'zustand';
import type { SortDirection } from '@scio3/shared-types';
import { SORT_DIRECTION, MODES } from '@scio3/shared-types';

export type PlaylistSortField = 'createdAt' | 'name' | 'lastUpdatedDate';

/** Subset of MODES used in playlist list: default browsing or shared folders view */
export type PlaylistListMode = typeof MODES.DEFAULT | typeof MODES.SHARE_WITH_ME;

interface PlaylistFilterState {
  searchText: string;
  searchScope: 'all' | 'folder';
  sortField: PlaylistSortField;
  sortDirection: SortDirection;
  mode: PlaylistListMode;
  /** When true, show only the current user's favorited playlists + folders (server-side). */
  favoritesOnly: boolean;
  /** Signal to scroll sidebar to a specific playlist after breadcrumb navigation */
  scrollToItemId: string | null;
  /** Whether the active playlist is currently visible in the sidebar scroll area */
  isItemVisible: boolean;

  setSearchText: (text: string) => void;
  setSearchScope: (scope: 'all' | 'folder') => void;
  setSort: (field: PlaylistSortField, direction: SortDirection) => void;
  setMode: (mode: PlaylistListMode) => void;
  setFavoritesOnly: (favoritesOnly: boolean) => void;
  clearFilters: () => void;
  hasActiveFilters: () => boolean;
  requestScrollToItem: (id: string) => void;
  clearScrollToItem: () => void;
  setItemVisible: (visible: boolean) => void;
}

const STORAGE_KEY = 'scio3-playlist-sorting';

function loadSort(): { sortField: PlaylistSortField; sortDirection: SortDirection } {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        sortField: parsed.sortField || 'createdAt',
        sortDirection: parsed.sortDirection || SORT_DIRECTION.DESC,
      };
    }
  } catch {
    // ignore parse errors
  }
  return { sortField: 'createdAt', sortDirection: SORT_DIRECTION.DESC };
}

const initial = loadSort();

export const usePlaylistFilterStore = create<PlaylistFilterState>((set, get) => ({
  searchText: '',
  searchScope: 'all',
  sortField: initial.sortField,
  sortDirection: initial.sortDirection,
  mode: MODES.DEFAULT,
  favoritesOnly: false,
  scrollToItemId: null,
  isItemVisible: false,

  setSearchText: (text) => set({ searchText: text }),
  setSearchScope: (scope) => set({ searchScope: scope }),
  setSort: (sortField, sortDirection) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ sortField, sortDirection }));
    set({ sortField, sortDirection });
  },
  setMode: (mode) => set({ mode, searchText: '', favoritesOnly: false }),
  setFavoritesOnly: (favoritesOnly) => set({ favoritesOnly }),
  clearFilters: () => set({ searchText: '', mode: MODES.DEFAULT, favoritesOnly: false }),
  hasActiveFilters: () => {
    const s = get();
    return s.searchText.length > 0 || s.mode === MODES.SHARE_WITH_ME || s.favoritesOnly;
  },
  requestScrollToItem: (id) => set({ scrollToItemId: id }),
  clearScrollToItem: () => set({ scrollToItemId: null }),
  setItemVisible: (visible) => {
    if (get().isItemVisible !== visible) set({ isItemVisible: visible });
  },
}));
